import { object, string, array, mixed, TestContext } from "yup";

import {
  ICompanyTypeSelectOption,
  IPlatformCompanySelectOption,
  IPrioritySelectOption,
} from "./Company";
import { IPipelineStageSelectOption } from "./Shared/Selects";
import { TypeOption, PersonValueItem } from "./Deal/Selects";
import { ICompanySelectOption, SourceCompanyOption } from "./Deal";
import { IThemeOption } from "@interfaces/sourcing.interface";
import { IAttribute, IAttributeFieldValue } from "@interfaces/attribute.interface";
import { validateAttributeValue } from "@components/Attributes";
import { UserForDropdown } from "@app-types/common.type";
import { IEmailAttachment } from "@interfaces/email.interface";
import { getErrorMessagesForDocuments } from "@utils/files.util";

export const URL_REGEX = /^(https?:\/\/)?(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?$/i;

// Max sourcing themes allowed on any entity (company, deal). Enforced via the yup schemas below
// (field-level RHF `rules` are ignored once a resolver is set) and surfaced by SourcingThemeFormField.
export const MAX_THEMES = 5;

// Attribute lists the `attribute_values` rules validate against; pass as `useForm({ context })`.
export type IAttributeValidationContext = {
  getAttributes: () => IAttribute[];
  // Only the deal dialog, which validates a deal's own attributes alongside the company's.
  getDealAttributes?: () => IAttribute[];
};

// Per-type attribute rules live in `validateAttributeValue`; they run here for the same reason as
// MAX_THEMES above — the `rules` on AttributesForm's Controller never fire alongside a resolver.
function testAttributeValue(
  test: TestContext,
  fieldValue: IAttributeFieldValue | undefined,
  source: keyof IAttributeValidationContext
) {
  const context = test.options.context as IAttributeValidationContext | undefined;
  const attribute = context?.[source]?.().find((a) => a.id === fieldValue?.attribute);

  if (!attribute) return true;

  const result = validateAttributeValue(attribute, String(fieldValue?.value ?? ""));

  return result === true || test.createError({ message: result });
}

const attributeValuesSchema = (source: keyof IAttributeValidationContext = "getAttributes") =>
  array()
    .of(
      mixed<IAttributeFieldValue>().test("attribute-value", function (fieldValue) {
        return testAttributeValue(this, fieldValue, source);
      })
    )
    .optional();

export const companySchema = object().shape({
  title: string().required("This field is required").min(3, "Title must be at least 3 characters"),
  website: string()
    .required("This field is required")
    .matches(URL_REGEX, "Please enter a complete website address"),
  priority: mixed<IPrioritySelectOption>().nullable().required("This field is required"),
  stage: mixed<IPipelineStageSelectOption>().required("This field is required"),
  type: mixed<ICompanyTypeSelectOption>().nullable().required("This field is required"),
  users: array().of(mixed<UserForDropdown>()).optional(),
  themes: array()
    .of(mixed<IThemeOption>())
    .max(MAX_THEMES, `Only ${MAX_THEMES} themes allowed`)
    .optional(),
  platform: mixed<IPlatformCompanySelectOption>().nullable().optional(),
  attribute_values: attributeValuesSchema(),
});

export const dealSchema = object().shape({
  title: string().required("This field is required").min(3, "Title must be at least 3 characters"),
  stage: mixed<IPipelineStageSelectOption>().required("This field is required"),
  company: mixed<ICompanySelectOption>().nullable().optional(),
  source_type: mixed<TypeOption>().nullable().optional(),
  source_company: mixed<SourceCompanyOption>().nullable().optional(),
  source_people: object()
    .shape({
      person: array().of(mixed<PersonValueItem>()).optional(),
      user: array().of(mixed<PersonValueItem>()).optional(),
    })
    .optional(),
  users: array().of(mixed<UserForDropdown>()).optional(),
  themes: array()
    .of(mixed<IThemeOption>())
    .max(MAX_THEMES, `Only ${MAX_THEMES} themes allowed`)
    .optional(),
  documents: array()
    .of(mixed<File>())
    .test("valid-documents", "Documents can't be uploaded", (files) => {
      const errors = getErrorMessagesForDocuments(files ?? []);
      return errors.length === 0;
    })
    .optional(),
  attachments: array().of(mixed<IEmailAttachment>()).optional(),
  attribute_values: attributeValuesSchema(),
  deal_attribute_values: attributeValuesSchema("getDealAttributes"),
});
